import classnames from 'classnames';
import React, { FC } from 'react';
import { FormattedMessage } from 'react-intl';
import { CloseButton, Text } from '../../../components/common';
import { ChevronDownSmall, ChevronUpSmall } from '../../../components/common/WixIcons';
import { ModalName } from '../../../constants/ModalName';
import { useTrackAddMediaSideBarActionsAnalytics } from '../../../utils/hooks/bi/useTrackAddMediaSideBarActionsAnalytics';
import { useBoolean } from '../../../utils/hooks/useBoolean';
import { useUploadingFiles } from '../../../utils/hooks/useFilesUploader';
import { useModalHandlersAndOptions } from '../../../utils/hooks/useModal';
import { memoizedOver } from '../../../utils/memoize';
import { FilesList } from './FilesList';
import styles from './UploadWindow.scss';
import { IOuter } from './UploadWindow.types';

export const UploadWindow: FC<IOuter> = ({
  onClearUploadingQueue,
  onOpenDestinationFolder,
  dataHook = 'upload-window',
  className,
  onForceClose,
  currentFolder,
}) => {
  const { value: isExpanded, toggle: toggleExpanded } = useBoolean(true);

  const {
    areAllFilesUploaded,
    hasAbortableFiles,
    filesCount,
    hasFailedFiles,
    invalidFilesCount,
    notAbortedFilesCount,
    uploadedFilesCount,
    hasNetworkErrorResumableFiles,
  } = useUploadingFiles();

  const shouldDisplayCancelAllButton = !areAllFilesUploaded && hasAbortableFiles;
  const shouldDisplayClearQueueButton = areAllFilesUploaded;

  const {
    trackAddMediaSideBarActionCancelAll,
    trackAddMediaSideBarActionClearList,
    trackAddMediaSideBarActionForceClearList,
  } = useTrackAddMediaSideBarActionsAnalytics();

  const { openModal: openCancelAllUploadingModal } = useModalHandlersAndOptions(ModalName.CancelAllUploads);

  return (
    <div className={classnames(styles.uploadWindow, className)} data-hook={dataHook}>
      <header className={styles.header}>
        <Text className={styles.title} light size="small" weight="bold" dataHook="add-media-panel-header-title">
          {areAllFilesUploaded ? (
            hasFailedFiles ? (
              invalidFilesCount === filesCount ? (
                hasNetworkErrorResumableFiles ? (
                  <FormattedMessage id="upload-window.header.upload-stopped-offline-title" />
                ) : (
                  <FormattedMessage id="upload-window.header.upload-stopped-title" />
                )
              ) : (
                <FormattedMessage
                  id="upload-window.header.uploaded-title"
                  values={{ uploadedFilesCount, totalFilesCount: notAbortedFilesCount }}
                />
              )
            ) : notAbortedFilesCount === 0 ? (
              <FormattedMessage id="upload-window.header.upload-canceled-title" />
            ) : (
              <FormattedMessage
                id="upload-window.header.successfully-uploaded-title"
                values={{ filesCount: notAbortedFilesCount }}
              />
            )
          ) : (
            <FormattedMessage
              id="upload-window.header.uploading-title"
              values={{ uploadedFilesCount, totalFilesCount: notAbortedFilesCount }}
            />
          )}
        </Text>

        <CloseButton
          className={styles.headerButton}
          skin="lightFilled"
          size="medium"
          onClick={toggleExpanded}
          dataHook="toggle-expand-button"
        >
          {isExpanded ? <ChevronDownSmall /> : <ChevronUpSmall />}
        </CloseButton>

        <CloseButton
          className={styles.headerButton}
          skin="lightFilled"
          size="medium"
          onClick={
            shouldDisplayCancelAllButton
              ? memoizedOver(openCancelAllUploadingModal, trackAddMediaSideBarActionCancelAll)
              : shouldDisplayClearQueueButton
              ? memoizedOver(onClearUploadingQueue, trackAddMediaSideBarActionClearList)
              : memoizedOver(onForceClose, trackAddMediaSideBarActionForceClearList)
          }
          dataHook={
            shouldDisplayCancelAllButton
              ? 'cancel-all-button'
              : shouldDisplayClearQueueButton
              ? 'clear-all-button'
              : 'force-close-button'
          }
        />
      </header>

      {isExpanded && (
        <div className={styles.filesList} data-hook="files-list-wrapper">
          <FilesList onOpenDestinationFolder={onOpenDestinationFolder} currentFolder={currentFolder} />
        </div>
      )}

      {!isExpanded && <div className={styles.filesListOverlay} onClick={toggleExpanded} />}
    </div>
  );
};
